"use client";

import * as React from "react";
import type { ComponentProps, HTMLAttributes } from "react";
import { memo } from "react";
import { Streamdown } from "streamdown";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
	TooltipContent,
	TooltipProvider,
	TooltipRoot,
	TooltipTrigger,
} from "../ui/tooltip";
import { isRelativeLinkOnly, type IsInternalLink } from "./link-safety";
import { MARKDOWN_CHROME } from "./markdown-chrome";
import { MarkdownControlTooltips } from "./markdown-control-tooltips";

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
	from: "user" | "assistant" | "system";
};

export const Message = ({ className, from, ...props }: MessageProps) => (
	<div
		className={cn(
			// The 95% cap is for the user's bubble, so it never runs the full width of
			// the column. An answer is not a bubble and should fill it.
			"group flex w-full flex-col gap-2",
			from === "user" && "max-w-[95%]",
			from === "user" ? "is-user ml-auto justify-end" : "is-assistant",
			className,
		)}
		{...props}
	/>
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement>;

/**
 * The bubble.
 *
 * Only the user's turn gets a fill; an answer renders straight onto the page.
 *
 * The two literal colours below are carried over verbatim from the original so
 * this port changes nothing visually. They should become design tokens before
 * any app adopts this — a raw hex cannot follow a theme.
 */
export const MessageContent = ({
	children,
	className,
	...props
}: MessageContentProps) => (
	<div
		className={cn(
			// `w-fit` for the user only. Their message is a bubble and has to hug its
			// text; the assistant's answer is not, so shrinking it to content left the
			// reasoning panel and any other full-width chrome sized to the shortest line
			// in the message. §7: only the user's own message keeps a bubble.
			"is-user:dark flex max-w-full min-w-0 flex-col gap-2 overflow-hidden text-[.9125rem]",
			"group-[.is-user]:w-fit group-[.is-assistant]:w-full",
			"group-[.is-user]:ml-auto group-[.is-user]:rounded-[1rem] group-[.is-user]:bg-chat-user-bubble group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-chat-foreground",
			"group-[.is-assistant]:text-chat-foreground",
			className,
		)}
		{...props}
	>
		{children}
	</div>
);

export type MessageActionsProps = ComponentProps<"div">;

export const MessageActions = ({
	className,
	children,
	...props
}: MessageActionsProps) => (
	<div className={cn("flex items-center gap-1", className)} {...props}>
		{children}
	</div>
);

export type MessageActionProps = ComponentProps<typeof Button> & {
	tooltip?: string;
	label?: string;
};

export const MessageAction = ({
	tooltip,
	children,
	label,
	variant = "ghost",
	size = "icon-sm",
	...props
}: MessageActionProps) => {
	const button = (
		<Button size={size} type="button" variant={variant} {...props}>
			{children}
			<span className="sr-only">{label || tooltip}</span>
		</Button>
	);

	if (!tooltip) {
		return button;
	}

	return (
		<TooltipProvider delayDuration={0}>
			<TooltipRoot>
				<TooltipTrigger asChild>{button}</TooltipTrigger>
				<TooltipContent>
					<p>{tooltip}</p>
				</TooltipContent>
			</TooltipRoot>
		</TooltipProvider>
	);
};

export type MessageResponseProps = Omit<
	ComponentProps<typeof Streamdown>,
	"linkSafety"
> & {
	/**
	 * Which links open without an interstitial.
	 *
	 * Defaults to relative paths only. A host with trusted hostnames builds one
	 * with `createIsInternalLink`.
	 */
	allowLink?: IsInternalLink;
};

/**
 * Markdown answer content.
 *
 * Memoised on the two props that actually change during a stream. Everything
 * else about a response is stable for its lifetime, and re-rendering the whole
 * markdown tree on every throttle tick is the difference between a smooth
 * stream and a janky one.
 */
export const MessageResponse = memo(
	({
		className,
		allowLink = isRelativeLinkOnly,
		...props
	}: MessageResponseProps) => (
		<MarkdownControlTooltips>
			<Streamdown
				// A plain per-word fade, per §9: "each chunk fading in over about 150ms. No
				// blur, slide, scale or stagger."
				//
				// This used to be a 350ms `blurIn`, taken from the Transitions.dev "Streaming
				// text" reference and marked in this comment as a deliberate deviation needing
				// an explicit call. APP-27605 made it: follow the criterion. A blur makes text
				// momentarily unreadable exactly where the eye already is, which costs more on
				// a long answer being read as it arrives than it does on a card appearing in a
				// list. The fade softens the arrival; it should not stage it.
				//
				// `sep` is left alone - streamdown already splits per word. `stagger` is still
				// passed rather than inherited, but its original reason is gone: 60ms was
				// chosen to keep the wave from outrunning a 350ms fade, and nothing has
				// re-tuned it against 150ms. §9 reads "or stagger", so this is the next thing
				// to settle here.
				animated={{
					animation: "fadeIn",
					duration: 150,
					stagger: 60,
					easing: "cubic-bezier(0.22, 1, 0.36, 1)",
				}}
				className={cn(
					"size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
					MARKDOWN_CHROME,
					className,
				)}
				linkSafety={{ enabled: true, onLinkCheck: allowLink }}
				{...props}
			/>
		</MarkdownControlTooltips>
	),
	(prev, next) =>
		prev.children === next.children && prev.isAnimating === next.isAnimating,
);

MessageResponse.displayName = "MessageResponse";
